/**
 * Ubicaciones cuyo producto va en el ticket "solo altura / Tronja 3": lo que hay que
 * bajar con medios (carretilla, escalera) y conviene preparar aparte.
 *
 * Reglas del almacén, configurables en public/env.js (y en el configmap de k8s):
 * - REACT_APP_ALTURA_PREFIJOS: prefijos separados por comas. Por defecto "T3" (Tronja 3,
 *   la misma marca que usa el backend para detectar productos de nave 2).
 * - REACT_APP_ALTURA_LETRAS: primera letra de los pasillos en altura, como letras o
 *   rangos separados por comas ("I-N", "I-N,Q"). Por defecto "I-N" (I a N, incluidas).
 * - REACT_APP_ETIQUETA_ALTURA: "true"/"false". Activa la etiqueta aparte de altura en Nave 2
 *   y en los grupos de Nave 2. Por defecto desactivada; solo "true" (o "1", "si") la enciende.
 *
 * DECISION: se leen en cada llamada y no al cargar el módulo, para que los tests puedan
 * cambiar window.env y para no depender del orden de carga de env.js.
 */
export const PREFIJOS_ALTURA_DEFECTO = 'T3';
export const LETRAS_ALTURA_DEFECTO = 'I-N';

/** Texto que identifica en el ticket impreso que no es el pedido completo */
export const AVISO_TICKET_ALTURA = 'SOLO PRODUCTOS EN ALTURA';

const leerEnv = (clave, defecto) => {
    const valor = typeof window !== 'undefined' ? window.env?.[clave] : undefined;
    return typeof valor === 'string' ? valor : defecto;
};

/**
 * Indica si se imprime la etiqueta aparte de altura (REACT_APP_ETIQUETA_ALTURA)
 * @returns {boolean} true solo si el flag es "true", "1" o "si"; sin definir, false
 */
export const etiquetaAlturaActiva = () =>
    ['TRUE', '1', 'SI', 'SÍ'].includes(leerEnv('REACT_APP_ETIQUETA_ALTURA', 'false').trim().toUpperCase());

const trocear = (texto) =>
    texto.split(',').map((parte) => parte.replace(/\s+/g, '').toUpperCase()).filter(Boolean);

/**
 * Prefijos de ubicación en altura (REACT_APP_ALTURA_PREFIJOS), en mayúsculas
 * @returns {Array<string>}
 */
export const prefijosAltura = () => trocear(leerEnv('REACT_APP_ALTURA_PREFIJOS', PREFIJOS_ALTURA_DEFECTO));

/**
 * Indica si una letra está en los pasillos en altura (REACT_APP_ALTURA_LETRAS). Los
 * trozos mal escritos (ni "X" ni "X-Y") se ignoran en vez de romper la comparación.
 * @param {string} letra - Una letra en mayúsculas
 * @returns {boolean}
 */
export const esLetraAltura = (letra) =>
    trocear(leerEnv('REACT_APP_ALTURA_LETRAS', LETRAS_ALTURA_DEFECTO)).some((trozo) => {
        const rango = /^([A-Z])(?:-([A-Z]))?$/.exec(trozo);
        if (!rango) return false;
        const [, desde, hasta = desde] = rango;
        return letra >= desde && letra <= hasta;
    });

/**
 * Indica si una ubicación es de altura o Tronja 3. Un artículo puede tener varias
 * ubicaciones en el mismo campo ("A12/T3-04"): basta con que una lo sea.
 * @param {string} ubicacion - Campo UBICACION del artículo
 * @returns {boolean}
 */
export const esUbicacionAlturaOTronja3 = (ubicacion) => {
    const prefijos = prefijosAltura();
    return String(ubicacion ?? '')
        .split(/[/,;]+/)
        .map((parte) => parte.replace(/\s+/g, '').toUpperCase())
        .filter(Boolean)
        .some((parte) => esLetraAltura(parte[0]) || prefijos.some((prefijo) => parte.startsWith(prefijo)));
};

/**
 * Productos del ticket (con `location`, como los de PrintOrder) que están en altura o Tronja 3
 * @param {Array<{location: string}>} productos
 * @returns {Array} Los productos que cumplen, en el mismo orden
 */
export const filtrarProductosAltura = (productos = []) =>
    productos.filter((producto) => esUbicacionAlturaOTronja3(producto.location));
